import { useMemo, useState } from "react";

import { Input } from "@/components/ui/input";
import type { FactSheet, Validation } from "@/api/schemas";
import { cn } from "@/lib/utils";

interface FactsTabProps {
  factSheet: FactSheet;
  validation: Validation;
}

const KEY_FACTS: { key: string; label: string; unit: string }[] = [
  { key: "bruto_total_m2", label: "Bruto ukupno", unit: "m²" },
  { key: "bruto_nadzemno_m2", label: "Bruto nadzemno", unit: "m²" },
  { key: "tlocrtna_projekcija_m2", label: "Tlocrtna projekcija", unit: "m²" },
  { key: "cestica_povrsina_m2", label: "Površina čestice", unit: "m²" },
  { key: "kig", label: "KIG", unit: "" },
  { key: "kisn", label: "KISN", unit: "" },
  { key: "zelena_povrsina_m2", label: "Zelena površina", unit: "m²" },
  { key: "zelena_pct", label: "Zeleni udio", unit: "" },
  { key: "total_glazed_area_m2", label: "Ukupna ostakljena pov.", unit: "m²" },
];

function formatNum(val: unknown): string {
  if (val == null) return "-";
  if (typeof val === "number") {
    return Number.isInteger(val) ? String(val) : val.toFixed(2).replace(".", ",");
  }
  return String(val);
}

/**
 * "Činjenice" tab — searchable readout of the fact sheet plus the
 * compliance check table and the validator output. Mirrors the legacy
 * ``renderFactSheet`` but with a search box and IFC origin badges.
 */
export function FactsTab({ factSheet, validation }: FactsTabProps) {
  const [query, setQuery] = useState("");

  const checks = (factSheet.compliance_checks ?? []) as Array<{
    parameter: string;
    actual: number | string | null;
    limit: number | string | null;
    check_type?: string;
    status?: string;
  }>;

  const sources = (factSheet._sources as Record<string, string> | undefined) ?? {};

  // L9: prefer the structured ``resolved_law_set`` produced by the
  // L4/L7 resolver. The legacy ``applicable_laws`` field stays as a
  // back-compat fallback for older fact sheets in saved projects.
  // ``LawRow`` lives at module scope so the ``LawTable`` helper can
  // reuse it without re-declaring.
  const resolvedSet = factSheet.resolved_law_set as
    | {
        cite_and_read?: LawRow[];
        cite_only?: LawRow[];
        recommended?: LawRow[];
      }
    | undefined;

  const mandatoryLaws: LawRow[] = resolvedSet
    ? [...(resolvedSet.cite_and_read ?? []), ...(resolvedSet.cite_only ?? [])]
    : ((factSheet.applicable_laws as LawRow[] | undefined) ?? []);
  const recommendedLaws: LawRow[] = resolvedSet?.recommended ?? [];

  const verificationPill = (
    status: string | undefined,
    lastVerified: string | null | undefined,
  ): { label: string; classes: string; tooltip: string } => {
    switch (status) {
      case "verified":
        return {
          label: "✓ provjereno",
          classes: "bg-green-50 text-green-700",
          tooltip: lastVerified
            ? `Posljednja sinkronizacija: ${lastVerified}`
            : "Sinkronizirano s katalogom NN portala.",
        };
      case "stale_text":
        return {
          label: "⌛ tekst zastario",
          classes: "bg-amber-50 text-amber-800",
          tooltip: lastVerified
            ? `Tekst nije osvježen od ${lastVerified}. Provjeriti protiv NN portala prije korištenja.`
            : "Tekst nije osvježen u zadnja 3 mjeseca.",
        };
      case "sync_overdue":
        return {
          label: "⛔ sync nije aktivan",
          classes: "bg-red-50 text-red-700",
          tooltip:
            "Noćna sinkronizacija s katalogom propisa nije se izvršila u zadnjih 36 h. Svi citati su sumnjivi dok se sinkronizacija ne pokrene.",
        };
      default:
        return {
          label: "⚠ neprovjereno",
          classes: "bg-stone-100 text-stone-600",
          tooltip:
            "NN chain nije ručno provjeren protiv NN portala niti dolazi iz centralnog kataloga.",
        };
    }
  };

  const filteredKeyFacts = useMemo(() => {
    const q = query.trim().toLowerCase();
    return KEY_FACTS.filter((f) => {
      if (!q) return true;
      return (
        f.label.toLowerCase().includes(q) ||
        f.key.toLowerCase().includes(q) ||
        formatNum(factSheet[f.key]).toLowerCase().includes(q)
      );
    });
  }, [factSheet, query]);

  return (
    <div className="space-y-6">
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Pretraži činjenice..."
        className="max-w-sm"
      />

      {checks.length > 0 && (
        <section>
          <h3 className="mb-3 text-[0.85rem] font-bold text-stone-700">
            Provjera usklađenosti s prostornim planom
          </h3>
          <div className="overflow-hidden rounded-md border border-stone-200">
            <table className="w-full text-[0.83rem]">
              <thead className="bg-stone-50 text-left text-[0.75rem] uppercase tracking-[0.04em] text-stone-500">
                <tr>
                  <th className="px-3 py-2">Parametar</th>
                  <th className="px-3 py-2">Projekt</th>
                  <th className="px-3 py-2">Ograničenje</th>
                  <th className="px-3 py-2">Tip</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((c, idx) => {
                  const ok = c.status === "OK";
                  return (
                    <tr key={idx} className="border-t border-stone-100">
                      <td className="px-3 py-2">{c.parameter}</td>
                      <td className="px-3 py-2 font-mono">{formatNum(c.actual)}</td>
                      <td className="px-3 py-2 font-mono">{formatNum(c.limit)}</td>
                      <td className="px-3 py-2">{c.check_type ?? ""}</td>
                      <td
                        className={cn(
                          "px-3 py-2 font-semibold",
                          ok ? "text-green-700" : "text-red-700",
                        )}
                      >
                        {ok ? "✓ OK" : "✗ PREKORAČENO"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section>
        <h3 className="mb-3 text-[0.85rem] font-bold text-stone-700">
          Izračunate vrijednosti
        </h3>
        <div className="overflow-hidden rounded-md border border-stone-200">
          <table className="w-full text-[0.83rem]">
            <tbody>
              {filteredKeyFacts.map((f) => {
                const raw = factSheet[f.key];
                if (raw == null) return null;
                const origin = sources[f.key];
                return (
                  <tr key={f.key} className="border-t border-stone-100 first:border-t-0">
                    <td className="w-1/3 px-3 py-2 text-stone-600">{f.label}</td>
                    <td className="px-3 py-2 font-mono text-stone-800">
                      {formatNum(raw)} {f.unit}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {origin === "ifc" && (
                        <span className="rounded bg-ind-50 px-1.5 py-0.5 text-[0.7rem] font-semibold text-ind-700">
                          IFC
                        </span>
                      )}
                      {origin === "user" && (
                        <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[0.7rem] font-semibold text-stone-500">
                          unos
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {mandatoryLaws.length > 0 && (
        <LawTable
          title={`Pravna osnova — obvezni propisi (${mandatoryLaws.length})`}
          rows={mandatoryLaws}
          verificationPill={verificationPill}
          showPrecisionChip
        />
      )}

      {recommendedLaws.length > 0 && (
        <LawTable
          title={`Preporučeni propisi (${recommendedLaws.length})`}
          rows={recommendedLaws}
          verificationPill={verificationPill}
          subdued
        />
      )}

      {validation.errors.length > 0 && (
        <section>
          <h3 className="mb-3 text-[0.85rem] font-bold text-red-700">
            Greške validacije ({validation.errors.length})
          </h3>
          <div className="space-y-1">
            {validation.errors.map((e, idx) => (
              <div
                key={idx}
                className="rounded border border-red-100 bg-red-50 px-3 py-2 text-[0.82rem] text-red-700"
              >
                <span className="font-mono text-[0.72rem] text-red-500">
                  [{e.section_id}]
                </span>{" "}
                {e.message}
              </div>
            ))}
          </div>
        </section>
      )}

      {validation.warnings.length > 0 && (
        <section>
          <h3 className="mb-3 text-[0.85rem] font-bold text-amber-700">
            Upozorenja ({validation.warnings.length})
          </h3>
          <div className="space-y-1">
            {validation.warnings.map((w, idx) => (
              <div
                key={idx}
                className="rounded border border-amber-100 bg-amber-50 px-3 py-2 text-[0.82rem] text-amber-800"
              >
                <span className="font-mono text-[0.72rem] text-amber-700">
                  [{w.section_id}]
                </span>{" "}
                {w.message}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * Render one bucket of resolved laws (mandatory or recommended). Pulled
 * out of the JSX above so the two buckets can share the table layout
 * including the L9 predicate tooltip and inline ``last_verified``
 * stamp without copy-pasting.
 */
type LawRow = {
  official_title?: string;
  short_title?: string;
  nn_chain?: string;
  formal_citation?: string;
  last_verified?: string | null;
  verification_status?:
    | "verified"
    | "stale_text"
    | "sync_overdue"
    | "unverified";
  matched_predicate?: unknown;
  notes?: string | null;
  applies_as?: string;
  source?: string;
  // Phase 3.8 — how well the rule's article numbering tracks the
  // current NN consolidation. See docs/CITATION_SCHEMA.md v1.1.
  article_precision?: "verified" | "unverified" | "unverified_no_text";
};

/**
 * Phase 3.8 — render a small, non-blocking precision chip next to
 * each mandatory citation. Colours follow the roadmap spec:
 *   verified           → green
 *   unverified         → amber/yellow
 *   unverified_no_text → neutral grey
 *
 * The chip is intentionally subtle (same xs size as the verification
 * pill, same baseline alignment) so it does NOT shift the row layout
 * versus a fact sheet emitted before schema v1.1. Missing field ->
 * no chip (back-compat with pre-3.8 fact sheets).
 */
function precisionChip(
  precision: LawRow["article_precision"],
): { label: string; classes: string; tooltip: string } | null {
  switch (precision) {
    case "verified":
      return {
        label: "čl. ✓",
        classes: "bg-green-50 text-green-700 ring-1 ring-inset ring-green-200",
        tooltip: "Tekst potvrđen za trenutnu NN",
      };
    case "unverified":
      return {
        label: "čl. ?",
        classes: "bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200",
        tooltip: "Numeracija članaka nije potvrđena za trenutnu NN",
      };
    case "unverified_no_text":
      return {
        label: "čl. —",
        classes: "bg-stone-100 text-stone-600 ring-1 ring-inset ring-stone-200",
        tooltip: "Samo naziv, tekst nije u snapshotu",
      };
    default:
      return null;
  }
}

function LawTable({
  title,
  rows,
  verificationPill,
  subdued = false,
  showPrecisionChip = false,
}: {
  title: string;
  rows: LawRow[];
  verificationPill: (
    status: string | undefined,
    lastVerified: string | null | undefined,
  ) => { label: string; classes: string; tooltip: string };
  subdued?: boolean;
  // Phase 3.8 — mandatory-bucket tables render an ``article_precision``
  // chip next to the verification pill. Recommended / informational
  // tables skip it to keep the advisory rows visually quiet.
  showPrecisionChip?: boolean;
}) {
  return (
    <section>
      <h3
        className={cn(
          "mb-3 text-[0.85rem] font-bold",
          subdued ? "text-stone-500" : "text-stone-700",
        )}
      >
        {title}
      </h3>
      <div className="overflow-hidden rounded-md border border-stone-200">
        <table className="w-full text-[0.83rem]">
          <thead className="bg-stone-50 text-left text-[0.75rem] uppercase tracking-[0.04em] text-stone-500">
            <tr>
              <th className="px-3 py-2">Akt</th>
              <th className="px-3 py-2">NN / SN chain</th>
              <th className="px-3 py-2">Provjereno</th>
              <th className="px-3 py-2 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((law, idx) => {
              const pill = verificationPill(
                law.verification_status,
                law.last_verified,
              );
              const why = explainPredicate(law);
              return (
                <tr key={idx} className="border-t border-stone-100">
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-stone-800">
                      {law.official_title ?? law.short_title ?? "—"}
                    </div>
                    {law.short_title && law.official_title && (
                      <div className="text-[0.72rem] uppercase tracking-[0.04em] text-stone-500">
                        {law.short_title}
                      </div>
                    )}
                    {why && (
                      <div
                        className="mt-1 inline-flex max-w-md cursor-help items-center gap-1 text-[0.7rem] text-stone-500"
                        title={why}
                      >
                        <span className="rounded bg-stone-100 px-1 py-0.5 font-mono">
                          ⓘ zašto
                        </span>
                        <span className="truncate">{why}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top font-mono text-[0.78rem] text-stone-700">
                    {law.nn_chain ?? law.formal_citation ?? "—"}
                  </td>
                  <td className="px-3 py-2 align-top font-mono text-[0.74rem] text-stone-500">
                    {law.last_verified ?? "—"}
                  </td>
                  <td className="px-3 py-2 align-top text-right">
                    <div className="flex flex-col items-end gap-1">
                      <span
                        className={cn(
                          "inline-flex items-center rounded px-1.5 py-0.5 text-[0.7rem] font-semibold",
                          pill.classes,
                        )}
                        title={pill.tooltip}
                      >
                        {pill.label}
                      </span>
                      {showPrecisionChip &&
                        (() => {
                          const chip = precisionChip(law.article_precision);
                          if (!chip) return null;
                          return (
                            <span
                              className={cn(
                                "inline-flex items-center rounded px-1.5 py-0.5 text-[0.68rem] font-medium",
                                chip.classes,
                              )}
                              title={chip.tooltip}
                              aria-label={chip.tooltip}
                            >
                              {chip.label}
                            </span>
                          );
                        })()}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Best-effort human translation of the predicate that triggered a
 * citation. The L4 predicate grammar is a closed JSON tree; the
 * curator-authored seed leans on a small handful of atoms (``always``,
 * ``context_contains``, ``feature``, ``building_type``,
 * ``building_type_pattern``, ``any``/``all``). We render those into a
 * single Croatian sentence; anything else falls back to the raw JSON
 * so the curator can still see it in the tooltip.
 */
function explainPredicate(law: LawRow): string {
  if (law.applies_as === "mandatory_always") return "Uvijek se primjenjuje.";
  if (law.notes && law.notes.startsWith("auto-expanded")) {
    return "Podzakonski akt vezan uz citirani matični zakon.";
  }
  const pred = law.matched_predicate;
  if (!pred || typeof pred !== "object") return "";
  const summary = summarisePredicate(pred as Record<string, unknown>);
  if (!summary) return "";
  return summary;
}

function summarisePredicate(pred: Record<string, unknown>): string {
  if ("always" in pred) return "Uvijek.";
  if ("never" in pred) return "Nikada.";
  if ("subordinate_of" in pred) {
    const data = pred.subordinate_of as { parent_short_title?: string };
    return data?.parent_short_title
      ? `Vezano uz ${data.parent_short_title}.`
      : "Vezano uz citirani matični zakon.";
  }
  if ("any" in pred && Array.isArray(pred.any)) {
    return (pred.any as Record<string, unknown>[])
      .map(summarisePredicate)
      .filter(Boolean)
      .join(" ili ");
  }
  if ("all" in pred && Array.isArray(pred.all)) {
    return (pred.all as Record<string, unknown>[])
      .map(summarisePredicate)
      .filter(Boolean)
      .join(" i ");
  }
  if ("not" in pred && pred.not && typeof pred.not === "object") {
    return `ne (${summarisePredicate(pred.not as Record<string, unknown>)})`;
  }
  if ("context_contains" in pred && Array.isArray(pred.context_contains)) {
    const samples = (pred.context_contains as unknown[]).slice(0, 2).join(", ");
    return `opis projekta sadrži "${samples}"`;
  }
  if ("building_type" in pred) {
    const v = pred.building_type;
    return Array.isArray(v) ? `tip građevine: ${v.join(", ")}` : `tip građevine: ${String(v)}`;
  }
  if ("building_type_pattern" in pred) {
    const v = pred.building_type_pattern;
    return Array.isArray(v)
      ? `opis građevine sadrži "${v.join(", ")}"`
      : `opis građevine sadrži "${String(v)}"`;
  }
  if ("work_type" in pred) {
    const v = pred.work_type;
    return Array.isArray(v) ? `vrsta zahvata: ${v.join(", ")}` : `vrsta zahvata: ${String(v)}`;
  }
  if ("designation_canonical" in pred) {
    const v = pred.designation_canonical;
    return Array.isArray(v) ? `namjena (oznaka): ${v.join(", ")}` : `namjena: ${String(v)}`;
  }
  if ("designation_prefix" in pred) {
    const v = pred.designation_prefix;
    return Array.isArray(v)
      ? `namjena počinje s "${v.join(", ")}"`
      : `namjena počinje s "${String(v)}"`;
  }
  if ("feature" in pred) {
    const flag = String(pred.feature);
    if ("is_true" in pred) return `kada je projektna značajka "${flag}" prisutna`;
    if ("is_false" in pred) return `kada projektna značajka "${flag}" nije prisutna`;
    return `značajka "${flag}"`;
  }
  return "";
}
